import React, { useMemo, useEffect } from 'react';
import { useLocalizedStrings } from '../../Localization';
import { Organization, useOrganizationContext } from '../../Contexts/OrganizationContext';
import { useUserContext } from '../../Contexts/UserContext';
import { Card, DataTable, Button, Icon } from '@aidkitorg/component-library';
import { TableAction } from '@aidkitorg/component-library/dist/types/src/DataTable';
import { AdminPanelPage } from '../AdminPanelPage';
import { ColumnDef } from '@tanstack/react-table';
import { useDebouncedCallback } from '../../Hooks/Debounce';
import { BreadcrumbProps } from '@aidkitorg/component-library';
import { type UserGroup } from '@aidkitorg/aidkit/lib/users/UserGroupService';
import { NavLink, useHistory } from 'react-router-dom';

export const UserGroupManagementPage: React.FC = () => {
  const L = useLocalizedStrings();
  const history = useHistory();

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.settings
    },
    {
      label: L.admin_panel.user_groups.title
    }
  ];

  const handleCreateUserGroup = () => {
    history.push('/admin-panel/user-management/user-groups/new');
  };

  const handleEditUserGroup = (userGroup: UserGroup) => {
    history.push(`/admin-panel/user-management/user-groups/${userGroup.id}`);
  };

  const pageActionButtons = [
    <Button key="create-user-group" onClick={handleCreateUserGroup} icon={<Icon
      name="PlusIcon"
      type="solid"
      role="decorative"
    />}>
      {L.admin_panel.user_groups.create_user_group}
    </Button>
  ];

  return (
    <AdminPanelPage
      title={L.admin_panel.user_groups.title}
      breadcrumbs={breadcrumbs}
      pageActionButtons={pageActionButtons}
    >
      <UserGroupsList
        onEditUserGroup={handleEditUserGroup}
      />
    </AdminPanelPage>
  );
};

type UserGroupsListProps = {
  onEditUserGroup: (userGroup: UserGroup) => void;
};

function UserGroupsList({ onEditUserGroup }: UserGroupsListProps) {
  const { selectedOrganization } = useOrganizationContext();
  const { userGroups, loadUserGroups, deleteUserGroup, isAdmin, isUserGroupsLoading } = useUserContext();
  const L = useLocalizedStrings();
  const history = useHistory();

  const debouncedLoadUserGroups = useDebouncedCallback(
    (organization: Organization | undefined | null) => {
      const organizationFilter = organization === null ? null : organization === undefined ? undefined : organization.uid;
      loadUserGroups(organizationFilter);
    },
    300,
    [loadUserGroups]
  );

  useEffect(() => {
    debouncedLoadUserGroups(selectedOrganization);
  }, [selectedOrganization]);

  const columns = useMemo(() => {
    const baseColumns: ColumnDef<UserGroup>[] = [
      {
        header: L.admin_panel.user_groups.group_name,
        accessorKey: 'name',
      },
      {
        header: L.admin_panel.user_groups.description,
        accessorKey: 'description',
        cell: ({ row }: any) => {
          const description = row.original.description;
          return description || '—';
        }
      },
      {
        header: L.admin_panel.user_groups.permissions,
        accessorKey: 'permissions',
        cell: ({ row }: any) => {
          const permissions = row.original.permissions;
          const permissionList = permissions ? permissions.split(',') : [];
          return permissionList.length > 0 ? `${permissionList.length} ${L.admin_panel.user_groups.permissions_count}` : '—';
        }
      },
      {
        header: L.admin_panel.user_groups.users_count,
        accessorKey: 'user_count',
        cell: ({ row }: any) => {
          const userCount = row.original.user_count || 0;
          const userGroupId = row.original.id;
          return (
            <NavLink
              to={`/admin-panel/user-management/users?user_group=${userGroupId}`}
              className="flex items-center space-x-1"
            >
              <Icon name="UserIcon" type="outline" role="decorative" className="inline-block" />
              <span>{userCount}</span>
            </NavLink>
          );
        }
      },
      {
        header: L.admin_panel.user_groups.created,
        accessorKey: 'created_at',
        cell: ({ row }: any) => {
          const createdAt = row.original.created_at;
          if (!createdAt) return '—';
          const date = new Date(createdAt);
          return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        }
      },
      {
        header: L.admin_panel.user_groups.last_updated,
        accessorKey: 'last_updated_at',
        cell: ({ row }: any) => {
          const metadata = row.original.metadata;
          const lastUpdatedAt = metadata?.last_updated_at;
          if (!lastUpdatedAt) return '—';
          const date = new Date(lastUpdatedAt);
          return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        }
      },
      {
        header: L.admin_panel.user_groups.organization,
        accessorKey: 'organization_name',
        cell: ({ row }: any) => {
          const orgName = row.original.organization_name;
          return orgName || '—';
        }
      },
    ];

    return baseColumns;
  }, [L, isAdmin]);

  const actions = useMemo((): TableAction<UserGroup>[] => [
    {
      label: L.admin_panel.user_groups.view_user_group,
      onClick: onEditUserGroup,
      icon: 'PencilIcon'
    },
    {
      label: L.admin_panel.user_groups.delete_user_group,
      onClick: async (userGroup) => {
        if (confirm(L.admin_panel.user_groups.confirm_delete_group)) {
          await deleteUserGroup(userGroup.id);
          loadUserGroups(selectedOrganization === null ? null : selectedOrganization?.uid);
        }
      },
      icon: 'TrashIcon',
      hideWhen: (userGroup) => userGroup.canEdit === false
    }
  ], [onEditUserGroup, deleteUserGroup, L, loadUserGroups, selectedOrganization]);

  return (
    <div className="w-full space-y-4">
      {/* User Groups table */}
      <Card>
        <DataTable
          pageSize={100}
          columns={columns}
          data={userGroups}
          actions={actions}
          loading={isUserGroupsLoading}
          enableSearch={true}
          searchLabel={L.admin_panel.user_groups.search}
          getRowId={(row: UserGroup, index: number) => {
            return row.id?.toString() ?? String(index);
          }}
        />
      </Card>
    </div>
  );
}

export default UserGroupManagementPage;